// pages/TestResultsPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import ErrorMessage from "../components/common/ErrorMessage";

const TestResultsPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [showQuestionReview, setShowQuestionReview] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    processTestResults();
  }, [testId]);

  // Process test results from localStorage or generate mock data
  const processTestResults = async () => {
    try {
      setLoading(true);
      
      // Fetch test details
      const testDoc = await getDoc(doc(db, "tests", testId));
      let testData;
      
      if (testDoc.exists()) {
        testData = { id: testDoc.id, ...testDoc.data() };
      } else {
        setError("Test not found!");
        setLoading(false);
        return;
      }
      
      setTest(testData);
      
      // Try to get real results from localStorage first
      const storedResults = localStorage.getItem(`testResults_${testId}`);
      let resultsData;
      
      if (storedResults) {
        // Process real test results
        resultsData = await processRealResults(JSON.parse(storedResults), testData);
      } else {
        // Generate mock results if no real results found
        resultsData = generateMockResults(testData);
      }
      
      setResults(resultsData);
      
      // Save results to Firestore
      try {
        await addDoc(collection(db, "testResults"), {
          ...resultsData,
          userId: auth.currentUser.uid,
          submittedAt: serverTimestamp()
        });
      } catch (err) {
        console.log("Could not save to Firestore:", err);
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Error processing test results:", err);
      setError("Failed to load test results. Please try again.");
      setLoading(false);
    }
  };

  // Process real test results from localStorage
  const processRealResults = async (storedResults, testData) => {
    const { answers, timeSpent } = storedResults;
    const questions = testData.questionsData || [];
    const totalQuestions = questions.length;
    const totalMarks = testData.marks || 20;
    
    let correctAnswers = 0;
    let incorrectAnswers = 0;
    let unattempted = 0;
    
    // Calculate scores and create question analysis
    const questionAnalysis = questions.map((question, index) => {
      const userAnswer = answers[index];
      const isCorrect = userAnswer === question.correctAnswer;
      const isAttempted = userAnswer !== null && userAnswer !== undefined;
      
      if (isAttempted) {
        if (isCorrect) {
          correctAnswers++;
        } else {
          incorrectAnswers++;
        }
      } else {
        unattempted++;
      }
      
      return {
        questionNo: index + 1,
        questionText: question.questionText,
        options: question.options,
        userAnswerIndex: userAnswer,
        correctAnswerIndex: question.correctAnswer,
        status: !isAttempted ? 'unattempted' : isCorrect ? 'correct' : 'incorrect',
        timeSpent: Math.floor(Math.random() * 120) + 30,
        difficulty: question.difficulty || 'Medium',
        topic: getTopicFromQuestion(question.questionText),
        marksAwarded: isCorrect ? (question.marks || 2) : 0,
        yourAnswer: isAttempted ? getOptionLetter(userAnswer) : 'Not Attempted',
        correctAnswer: getOptionLetter(question.correctAnswer),
        explanation: question.explanation
      };
    });
    
    const obtainedMarks = correctAnswers * (testData.marks / totalQuestions);
    const percentage = Math.floor((obtainedMarks / totalMarks) * 100);
    const accuracy = Math.floor((correctAnswers / (correctAnswers + incorrectAnswers || 1)) * 100);
    
    // Generate topic-wise performance
    const topicPerformance = generateTopicPerformance(questionAnalysis);
    
    const timeTakenMinutes = Math.floor(timeSpent / 60);
    
    return {
      testId,
      testTitle: testData.title,
      studentName: "Student",
      submittedAt: new Date(),
      timeTaken: `${timeTakenMinutes} minutes`,
      totalQuestions,
      totalMarks,
      obtainedMarks: Math.floor(obtainedMarks),
      percentage,
      grade: percentage >= 90 ? 'A+' : percentage >= 80 ? 'A' : percentage >= 70 ? 'B+' : percentage >= 60 ? 'B' : percentage >= 50 ? 'C' : 'F',
      correctAnswers,
      incorrectAnswers,
      unattempted,
      accuracy,
      rank: Math.floor(Math.random() * 100) + 1,
      totalAppeared: Math.floor(Math.random() * 500) + 200,
      topicPerformance,
      questionAnalysis,
      strengths: topicPerformance.filter(t => t.accuracy > 70).map(t => t.topic),
      weaknesses: topicPerformance.filter(t => t.accuracy < 50).map(t => t.topic),
      recommendations: generateRecommendations(percentage, accuracy, topicPerformance)
    };
  };

  // Generate mock results for fallback
  const generateMockResults = (testData) => {
    const questions = testData.questionsData || [];
    const totalQuestions = questions.length || testData.questions || 10;
    const totalMarks = testData.marks || 20;
    const correctAnswers = Math.floor(Math.random() * (totalQuestions - totalQuestions * 0.3)) + Math.floor(totalQuestions * 0.6);
    const incorrectAnswers = Math.floor(Math.random() * (totalQuestions - correctAnswers));
    const unattempted = totalQuestions - correctAnswers - incorrectAnswers;
    const marksPerQuestion = totalMarks / totalQuestions;
    const obtainedMarks = Math.floor(correctAnswers * marksPerQuestion);
    const percentage = Math.floor((obtainedMarks / totalMarks) * 100);
    const timeTaken = Math.floor(Math.random() * 25) + 15;
    
    const topics = getTestTopics(testData.title);
    
    const topicPerformance = topics.map(topic => ({
      topic,
      attempted: Math.floor(Math.random() * 4) + 2,
      correct: Math.floor(Math.random() * 3) + 1,
      total: Math.floor(Math.random() * 2) + 3
    })).map(item => ({
      ...item,
      accuracy: Math.floor((item.correct / item.attempted) * 100)
    }));
    
    const questionAnalysis = questions.length > 0 ? questions.map((question, index) => {
      const status = Math.random();
      const isCorrect = status > 0.7;
      const isAttempted = status > 0.4;
      const userAnswerIndex = isAttempted ? Math.floor(Math.random() * 4) : null;
      
      return {
        questionNo: index + 1,
        questionText: question.questionText,
        options: question.options,
        userAnswerIndex: userAnswerIndex,
        correctAnswerIndex: question.correctAnswer,
        status: !isAttempted ? 'unattempted' : isCorrect ? 'correct' : 'incorrect',
        timeSpent: Math.floor(Math.random() * 120) + 30,
        difficulty: question.difficulty || 'Medium',
        topic: getTopicFromQuestion(question.questionText),
        marksAwarded: isCorrect ? marksPerQuestion : 0,
        yourAnswer: isAttempted ? getOptionLetter(userAnswerIndex) : 'Not Attempted',
        correctAnswer: getOptionLetter(question.correctAnswer),
        explanation: question.explanation
      };
    }) : Array.from({ length: totalQuestions }, (_, index) => {
      const status = Math.random();
      return {
        questionNo: index + 1,
        questionText: `Sample question ${index + 1}`,
        options: ['Option A', 'Option B', 'Option C', 'Option D'],
        userAnswerIndex: status > 0.4 ? Math.floor(Math.random() * 4) : null,
        correctAnswerIndex: Math.floor(Math.random() * 4),
        status: status > 0.7 ? 'correct' : status > 0.4 ? 'incorrect' : 'unattempted',
        timeSpent: Math.floor(Math.random() * 120) + 30,
        difficulty: ['Easy', 'Medium', 'Hard'][Math.floor(Math.random() * 3)],
        topic: topics[Math.floor(Math.random() * topics.length)],
        marksAwarded: status > 0.7 ? marksPerQuestion : 0,
        yourAnswer: status > 0.4 ? String.fromCharCode(65 + Math.floor(Math.random() * 4)) : 'Not Attempted',
        correctAnswer: String.fromCharCode(65 + Math.floor(Math.random() * 4)),
        explanation: "This is a sample explanation for the question."
      };
    });
    
    return {
      testId,
      testTitle: testData.title,
      studentName: "Student",
      submittedAt: new Date(),
      timeTaken: `${timeTaken} minutes`,
      totalQuestions,
      totalMarks,
      obtainedMarks,
      percentage,
      grade: percentage >= 90 ? 'A+' : percentage >= 80 ? 'A' : percentage >= 70 ? 'B+' : percentage >= 60 ? 'B' : percentage >= 50 ? 'C' : 'F',
      correctAnswers,
      incorrectAnswers,
      unattempted,
      accuracy: Math.floor((correctAnswers / totalQuestions) * 100),
      rank: Math.floor(Math.random() * 1000) + 1,
      totalAppeared: Math.floor(Math.random() * 2000) + 1500,
      topicPerformance,
      questionAnalysis,
      strengths: topicPerformance.filter(t => t.accuracy > 70).map(t => t.topic),
      weaknesses: topicPerformance.filter(t => t.accuracy < 50).map(t => t.topic),
      recommendations: generateRecommendations(percentage, Math.floor((correctAnswers / totalQuestions) * 100), topicPerformance)
    };
  };

  // Helper functions
  const getOptionLetter = (optionIndex) => {
    if (optionIndex === null || optionIndex === undefined) return 'Not Attempted';
    return String.fromCharCode(65 + optionIndex);
  };

  const getTopicFromQuestion = (questionText) => {
    if (questionText.toLowerCase().includes('constantinople')) return 'Constantinople & Trade Routes';
    if (questionText.toLowerCase().includes('byzantine') || questionText.toLowerCase().includes('empire')) return 'Byzantine Empire';
    if (questionText.toLowerCase().includes('spice') || questionText.toLowerCase().includes('trade')) return 'Spice Trade';
    if (questionText.toLowerCase().includes('turk') || questionText.toLowerCase().includes('tax')) return 'Ottoman Control';
    if (questionText.toLowerCase().includes('sea route') || questionText.toLowerCase().includes('alternative')) return 'Exploration & Routes';
    return 'European History';
  };

  const getTestTopics = (testTitle) => {
    if (testTitle.toLowerCase().includes('european')) {
      return ['Constantinople & Trade Routes', 'Byzantine Empire', 'Spice Trade', 'Ottoman Control', 'Exploration & Routes'];
    }
    return ['Topic 1', 'Topic 2', 'Topic 3', 'Topic 4', 'Topic 5'];
  };

  const generateTopicPerformance = (questionAnalysis) => {
    const topicMap = {};
    
    questionAnalysis.forEach(q => {
      if (!topicMap[q.topic]) {
        topicMap[q.topic] = { attempted: 0, correct: 0, total: 0 };
      }
      topicMap[q.topic].total++;
      if (q.status !== 'unattempted') {
        topicMap[q.topic].attempted++;
        if (q.status === 'correct') {
          topicMap[q.topic].correct++;
        }
      }
    });

    return Object.entries(topicMap).map(([topic, data]) => ({
      topic,
      attempted: data.attempted,
      correct: data.correct,
      total: data.total,
      accuracy: data.attempted > 0 ? Math.floor((data.correct / data.attempted) * 100) : 0
    }));
  };

  const generateRecommendations = (percentage, accuracy, topicPerformance) => {
    const recommendations = [];
    
    if (percentage < 60) {
      recommendations.push("Focus on understanding fundamental concepts before attempting advanced topics");
    }
    if (accuracy < 70) {
      recommendations.push("Practice more questions to improve accuracy and reduce silly mistakes");
    }
    if (topicPerformance.some(t => t.accuracy < 50)) {
      recommendations.push("Identify and strengthen weak topic areas through targeted practice");
    }
    recommendations.push("Review explanations for incorrect answers to understand the concepts better");
    recommendations.push("Take more mock tests to build confidence and improve time management");
    
    return recommendations;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-blue-600 font-medium">Processing your results...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return <ErrorMessage message={error} />;
  }

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+': case 'A': return 'text-green-600 bg-green-100';
      case 'B+': case 'B': return 'text-blue-600 bg-blue-100';
      case 'C': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'text-green-600 bg-green-100';
      case 'incorrect': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="pt-20 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Test Results</h1>
            <div className="flex space-x-3">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print
              </button>
              <Link
                to="/test-series"
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Take Another Test
              </Link>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{test.title}</h2>
              <p className="text-gray-600">Submitted on: {results.submittedAt.toLocaleDateString()}</p>
              <p className="text-gray-600">Time Taken: {results.timeTaken}</p>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-4 py-2 rounded-full text-2xl font-bold ${getGradeColor(results.grade)}`}>
                Grade: {results.grade}
              </div>
              <p className="text-gray-600 mt-2">Rank: {results.rank} / {results.totalAppeared}</p>
            </div>
          </div>
        </motion.div>

        {/* Score Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">{results.obtainedMarks}</div>
            <div className="text-gray-600">Total Score</div>
            <div className="text-sm text-gray-500">out of {results.totalMarks}</div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">{results.percentage}%</div>
            <div className="text-gray-600">Percentage</div>
            <div className="text-sm text-gray-500">Overall Performance</div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">{results.accuracy}%</div>
            <div className="text-gray-600">Accuracy</div>
            <div className="text-sm text-gray-500">{results.correctAnswers}/{results.totalQuestions} correct</div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">#{results.rank}</div>
            <div className="text-gray-600">Your Rank</div>
            <div className="text-sm text-gray-500">out of {results.totalAppeared}</div>
          </div>
        </motion.div>

        {/* Performance Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
        >
          {/* Question Status */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">Question Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                  <span>Correct Answers</span>
                </div>
                <span className="font-semibold">{results.correctAnswers}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                  <span>Incorrect Answers</span>
                </div>
                <span className="font-semibold">{results.incorrectAnswers}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-gray-400 rounded-full mr-3"></div>
                  <span>Unattempted</span>
                </div>
                <span className="font-semibold">{results.unattempted}</span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex rounded-full overflow-hidden h-4">
                <div 
                  className="bg-green-500" 
                  style={{ width: `${(results.correctAnswers / results.totalQuestions) * 100}%` }}
                ></div>
                <div 
                  className="bg-red-500" 
                  style={{ width: `${(results.incorrectAnswers / results.totalQuestions) * 100}%` }}
                ></div>
                <div 
                  className="bg-gray-400" 
                  style={{ width: `${(results.unattempted / results.totalQuestions) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Topic Performance */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">Performance by Topic</h3>
            <div className="space-y-3">
              {results.topicPerformance.map((topic, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{topic.topic}</div>
                    <div className="text-sm text-gray-600">{topic.correct}/{topic.attempted} correct</div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className={`h-2 rounded-full ${topic.accuracy >= 70 ? 'bg-green-500' : topic.accuracy >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${topic.accuracy}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{topic.accuracy}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Strengths and Weaknesses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8"
        >
          {/* Strengths */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4 text-green-700">Your Strengths</h3>
            <div className="space-y-2">
              {results.strengths.length > 0 ? results.strengths.map((strength, index) => (
                <div key={index} className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{strength}</span>
                </div>
              )) : (
                <p className="text-gray-600">Keep practicing to identify your strengths!</p>
              )}
            </div>
          </div>

          {/* Weaknesses */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4 text-red-700">Areas to Improve</h3>
            <div className="space-y-2">
              {results.weaknesses.length > 0 ? results.weaknesses.map((weakness, index) => (
                <div key={index} className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>{weakness}</span>
                </div>
              )) : (
                <p className="text-gray-600">Great job! No major weaknesses identified.</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white p-6 rounded-lg shadow-md mb-8"
        >
          <h3 className="text-xl font-semibold mb-4">Recommendations for Improvement</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.recommendations.map((recommendation, index) => (
              <div key={index} className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-gray-700">{recommendation}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Detailed Question Analysis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-white p-6 rounded-lg shadow-md mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Question-wise Analysis</h3>
            <button
              onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {showDetailedAnalysis ? 'Hide' : 'Show'} Details
              <svg className={`ml-1 w-4 h-4 transform ${showDetailedAnalysis ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {showDetailedAnalysis ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Question
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Topic
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Your Answer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Correct Answer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Spent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Marks
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.questionAnalysis.map((question, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Q{question.questionNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {question.topic}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {question.yourAnswer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {question.correctAnswer}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(question.status)}`}>
                          {question.status.charAt(0).toUpperCase() + question.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {Math.floor(question.timeSpent / 60)}m {question.timeSpent % 60}s
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {question.marksAwarded || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.questionAnalysis.slice(0, 6).map((question, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">Q{question.questionNo}</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(question.status)}`}>
                      {question.status.charAt(0).toUpperCase() + question.status.slice(1)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">{question.topic}</div>
                  <div className="text-xs text-gray-500">
                    Your Answer: {question.yourAnswer} | Correct: {question.correctAnswer}
                  </div>
                </div>
              ))}
              {results.questionAnalysis.length > 6 && (
                <div className="col-span-full text-center">
                  <button
                    onClick={() => setShowDetailedAnalysis(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View all {results.questionAnalysis.length} questions
                  </button>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Question Review Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-white p-6 rounded-lg shadow-md mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Detailed Question Review</h3>
            <button
              onClick={() => setShowQuestionReview(!showQuestionReview)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {showQuestionReview ? 'Hide' : 'Show'} Questions
              <svg className={`ml-1 w-4 h-4 transform ${showQuestionReview ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {showQuestionReview && (
            <div className="space-y-6">
              {results.questionAnalysis.map((question, index) => (
                <div key={index} className="border rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-lg font-semibold text-gray-900 mr-3">
                          Question {question.questionNo}
                        </span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(question.status)}`}>
                          {question.status.charAt(0).toUpperCase() + question.status.slice(1)}
                        </span>
                        <span className="ml-2 text-sm text-gray-500">
                          {question.difficulty} | {question.topic}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-4">{question.questionText}</p>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-sm text-gray-500">Marks: {question.marksAwarded || 0}</div>
                      <div className="text-sm text-gray-500">Time: {Math.floor(question.timeSpent / 60)}m {question.timeSpent % 60}s</div>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                    {question.options?.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        className={`p-3 rounded-lg border-2 ${
                          optionIndex === question.correctAnswerIndex
                            ? 'border-green-500 bg-green-50'
                            : optionIndex === question.userAnswerIndex && question.status === 'incorrect'
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="font-medium mr-2">
                            {String.fromCharCode(65 + optionIndex)}.
                          </span>
                          <span>{option}</span>
                          {optionIndex === question.correctAnswerIndex && (
                            <svg className="w-5 h-5 text-green-500 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {optionIndex === question.userAnswerIndex && question.status === 'incorrect' && (
                            <svg className="w-5 h-5 text-red-500 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Answer Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Your Answer: </span>
                        <span className={`font-semibold ${question.status === 'correct' ? 'text-green-600' : question.status === 'incorrect' ? 'text-red-600' : 'text-gray-600'}`}>
                          {question.yourAnswer}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Correct Answer: </span>
                        <span className="font-semibold text-green-600">{question.correctAnswer}</span>
                      </div>
                    </div>
                  </div>

                  {/* Explanation */}
                  {question.explanation && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">Explanation:</h4>
                      <p className="text-sm text-blue-700">{question.explanation}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Link
            to="/test-series"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Take Another Test
          </Link>
          
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Back to Dashboard
          </button>

          <button
            onClick={() => window.print()}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Results
          </button>
        </motion.div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="text-center mt-8 text-gray-500 text-sm"
        >
          <p>Results are automatically saved. You can view them anytime from your dashboard.</p>
        </motion.div>
      </div>
    </div>
  );
};

export default TestResultsPage;